import { Buildable } from "ts-essentials";
import {
    Contract,
    Repetition,
    ScheduleTimeType,
    ScheduleType,
    Unit,
    WorkingTimeClassicType,
} from "../../__generated__/graphql";
import { useCallback } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { MINIMUM_SLOT_DURATION_HOURS } from "../../utils";
import { EnabledWorkingTimeSpecificType } from "./TimeSheetSpecificDatesComponent";

export const setContractWorkingTimeClassicSpec = (
    contract: Buildable<Contract>,
    newValue: Buildable<WorkingTimeClassicType>,
): Buildable<Contract> => {
    return {
        ...contract,
        workingTimeClassicSpec: newValue,
    } as Buildable<Contract>;
};

export type WorkingTimeComponentProps = {
    disabled?: boolean;
    contract: Buildable<Contract>;
    setContract: (m: Buildable<Contract>) => void;
};

export const MIN_HOURS_PER_WEEK_FULL_TIME = 35;
export const MAX_NB_DAYS_PER_WEEK = 7;
export const MAX_NB_DAYS_PER_FIXED_MISSION = 23;
export const MAX_DAILY_HOURS = 24;

export const maxHoursPerWeekForRepetition = (
    repetition: Repetition,
    hoursPerWeek: number,
): number => {
    switch (repetition) {
        case Repetition.Week:
            return hoursPerWeek;
        case Repetition.Week2:
            return hoursPerWeek;
        case Repetition.Week3:
            return hoursPerWeek;
        case Repetition.Week4:
            return hoursPerWeek;
        case Repetition.Month:
            return hoursPerWeek * 5;
        case Repetition.None:
            return hoursPerWeek * 5;
    }
    return hoursPerWeek;
};

export const maxNbDays = (repetition?: Repetition) => {
    return (
        repetition && ![Repetition.None, Repetition.Month].includes(repetition)
            ? MAX_NB_DAYS_PER_WEEK
            : MAX_NB_DAYS_PER_FIXED_MISSION
    ) as number;
};

export type WorkingTimeClassicSpecErrorType = {
    errorRepetition: boolean | undefined | string;
    errorUnitCount: boolean | undefined | string;
    errorDailyHours: boolean | undefined | string;
    hasError: boolean;
};

export const useHasErrorWorkingTimeClassicSpec = () => {
    const i18n = useI18n();
    return useCallback(
        (
            unit: Unit,
            repetition: Repetition | undefined,
            unitCount: number | undefined,
            hoursPerWeek: number,
            dailyHours: number | undefined,
        ): WorkingTimeClassicSpecErrorType => {
            let errorRepetition = undefined;
            let errorUnitCount = undefined;
            let errorDailyHours = undefined;
            if (!repetition) {
                errorRepetition = i18n.t("utils.working_time.classic.error.repetition_not_set");
            } else {
                if (!unitCount) {
                    errorUnitCount = i18n.t("utils.working_time.classic.error.unit_count_not_set");
                }
            }
            if (unit === Unit.Days && dailyHours === undefined) {
                errorDailyHours = i18n.t("utils.working_time.classic.error.daily_hours_not_set");
            }
            if (repetition && unitCount) {
                const max =
                    unit === Unit.Hours
                        ? maxHoursPerWeekForRepetition(repetition, hoursPerWeek)
                        : maxNbDays(repetition);
                if (unitCount < 0 || unitCount > max) {
                    errorUnitCount = i18n.t(
                        `utils.working_time.classic.error.${unit}_unit_count_too_high`,
                        {
                            count: max,
                        },
                    );
                }
            }
            if (dailyHours !== undefined && (dailyHours <= 0 || dailyHours > MAX_DAILY_HOURS)) {
                errorDailyHours = i18n.t("utils.working_time.classic.error.daily_hours_too_high", {
                    count: MAX_DAILY_HOURS,
                });
            }
            const hasError = !!errorRepetition || !!errorUnitCount || !!errorDailyHours;
            return {
                errorRepetition: errorRepetition,
                errorUnitCount: errorUnitCount,
                errorDailyHours: errorDailyHours,
                hasError,
            };
        },
        [i18n],
    );
};

export const STANDARD_WORKING_TIME_CLASSIC_SPEC = {
    repetition: Repetition.Week,
    unitCount: 42,
    unit: Unit.Hours,
};

export type WorkingTimeSpecificType = {
    errors: { [key: string]: boolean };
    timeErrors: Array<ScheduleTypeErrorInfo | null>;
    hasError: boolean;
};

export type ScheduleTypeErrorInfo = {
    errors: { [key: string]: boolean };
    indexErrors: Array<string[]>;
    hasError: boolean;
};

export const checkScheduleType = (
    dates: EnabledWorkingTimeSpecificType[],
    vacationOverflow?: number,
): WorkingTimeSpecificType => {
    const result = {
        errors: {} as { [key: string]: boolean },
        indexErrors: Array.from({ length: dates.length }, () => [] as string[]),
        timeErrors: Array.from(
            { length: dates.length },
            () => null as ScheduleTypeErrorInfo | null,
        ),
        hasError: false,
    };
    const rDates = dates || [];

    // check if slot is smaller than min slot duration
    rDates.forEach((x, idx) => {
        if (x.enabled) {
            const ret = checkScheduleTypeList(x?.daySchedule?.fixedTimes, vacationOverflow);
            if (ret.hasError) {
                result.errors["workingTime.invalidTime"] = true;
            }
            result.timeErrors[idx] = ret;
        }
    });
    result.hasError = Object.keys(result.errors).length > 0;
    return result;
};

export const checkScheduleTypeList = (
    times?: Array<Buildable<ScheduleType> | undefined> | null,
    vacationOverflow?: number,
): ScheduleTypeErrorInfo => {
    const result = {
        errors: {} as { [key: string]: boolean },
        // map times and return an array of empty arrays
        indexErrors: Array.from({ length: times?.length || 0 }, () => [] as string[]),
        hasError: false,
    };
    const rTimes = times || [];

    // check if slot is smaller than min slot duration
    rTimes.forEach((x, idx) => {
        if (x && (x.endTime || 0) - (x.startTime || 0) < MINIMUM_SLOT_DURATION_HOURS * 60) {
            result.errors["workingTime.invalidTime"] = true;
            result.indexErrors[idx] = [
                ...(result.indexErrors[idx] || []),
                "workingTime.tooLowDuration",
            ];
        }
    });

    // check if there is any overlap
    rTimes.forEach((x, idx) => {
        if (vacationOverflow && vacationOverflow > 0 && x && x.type === ScheduleTimeType.Vacation) {
            result.errors["workingTime.insufficient_vacation"] = true;
            result.indexErrors[idx] = [
                ...(result.indexErrors[idx] || []),
                "workingTime.insufficient_vacation",
            ];
        }
        rTimes.forEach((y, idy) => {
            if (idx !== idy) {
                if (
                    x &&
                    y &&
                    (x.startTime || 0) < (y.endTime || 0) &&
                    (x.endTime || 0) > (y.startTime || 0)
                ) {
                    result.errors["workingTime.overlapError"] = true;
                    result.indexErrors[idx] = [
                        ...new Set([
                            ...(result.indexErrors[idx] || []),
                            "workingTime.overlapError",
                        ]),
                    ];
                }
            }
        });
    });
    result.hasError = Object.keys(result.errors).length > 0;
    return result;
};
